// Date tokens for check-in/check-out style filters. Tokens stay relative
// (`wknd`, `+7`) until build time, so a preset like `!rec.wknd` always means
// the coming weekend.

export interface DateRange {
  start: Date;
  end: Date;
  /** One date was given (`10/17`, `tmrw`); engines that need a range add a night. */
  single: boolean;
}

const MD = /^(\d{1,2})\/(\d{1,2})$/;
const RANGE = /^(\d{1,2})\/(\d{1,2})-(\d{1,2})\/(\d{1,2})$/;
const PLUS = /^\+(\d{1,2})$/;

function day(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function addDays(d: Date, n: number): Date {
  const out = day(d);
  out.setDate(out.getDate() + n);
  return out;
}

/** Month/day on or after `from`, rolling into next year if it has passed. */
function nextOccurrence(
  month: number,
  date: number,
  from: Date
): Date | undefined {
  if (month < 1 || month > 12 || date < 1 || date > 31) return undefined;
  // Up to four years ahead, so 2/29 finds the next leap year; a date no
  // year has (2/30) finds nothing.
  for (let year = from.getFullYear(); year <= from.getFullYear() + 4; year++) {
    const d = new Date(year, month - 1, date);
    if (d.getMonth() !== month - 1) continue;
    if (d >= day(from)) return d;
  }
  return undefined;
}

export function resolveDates(
  token: string,
  now = new Date()
): DateRange | undefined {
  const today = day(now);
  if (token === 'tmrw') {
    const t = addDays(today, 1);
    return { start: t, end: t, single: true };
  }
  if (token === 'wknd') {
    // Friday through Sunday; on Fri/Sat that's this weekend.
    const dow = today.getDay();
    const toFriday = dow === 5 ? 0 : dow === 6 ? -1 : (5 - dow + 7) % 7;
    const start = addDays(today, toFriday);
    return {
      start: dow === 6 ? today : start,
      end: addDays(start, 2),
      single: false,
    };
  }
  const plus = PLUS.exec(token);
  if (plus) {
    const n = Number(plus[1]);
    if (n < 1 || n > 90) return undefined;
    return { start: today, end: addDays(today, n), single: false };
  }
  const single = MD.exec(token);
  if (single) {
    const d = nextOccurrence(Number(single[1]), Number(single[2]), today);
    return d && { start: d, end: d, single: true };
  }
  const range = RANGE.exec(token);
  if (range) {
    const start = nextOccurrence(Number(range[1]), Number(range[2]), today);
    const end =
      start && nextOccurrence(Number(range[3]), Number(range[4]), start);
    if (!start || !end) return undefined;
    // Ranges may wrap the new year (12/28-1/3), but a reversed one
    // (10/17-10/16) would otherwise read as a year-long stay.
    if (end.getTime() - start.getTime() > 200 * 864e5) return undefined;
    // 11/20-11/20 is one date, not a zero-night stay.
    if (end.getTime() === start.getTime()) return { start, end, single: true };
    return { start, end, single: false };
  }
  return undefined;
}

export function isDateToken(token: string): boolean {
  return resolveDates(token) !== undefined;
}

/** Start and end, with one night added when a single date was given. */
export function stayRange(
  token: string,
  now = new Date()
): { start: Date; end: Date } | undefined {
  const r = resolveDates(token, now);
  if (!r) return undefined;
  return { start: r.start, end: r.single ? addDays(r.start, 1) : r.end };
}

const pad = (n: number) => String(n).padStart(2, '0');

export function isoDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function usDate(d: Date): string {
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
}

/** Token for an explicit start/end pair from the builder's date inputs. */
export function rangeToken(start: Date, end?: Date): string {
  const md = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;
  return end && end.getTime() !== start.getTime()
    ? `${md(start)}-${md(end)}`
    : md(start);
}

const fmt = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
});

export function describeDates(token: string, now = new Date()): string {
  const r = resolveDates(token, now);
  if (!r) return token;
  const label =
    token === 'wknd'
      ? 'Next weekend'
      : token === 'tmrw'
        ? 'Tomorrow'
        : token.startsWith('+')
          ? `Next ${token.slice(1)} days`
          : '';
  const span = r.single
    ? fmt.format(r.start)
    : `${fmt.format(r.start)}–${fmt.format(r.end)}`;
  return label ? `${label} (${span})` : span;
}
